import { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Accordion from "react-bootstrap/Accordion";
import ServiceOption from "components/Customize/ServiceOption";
import ChannelGuide from "components/Browse/ChannelGuide";
import INT_ICON_URL from "images/internet-icon.png";
import VID_ICON_URL from "images/tv-icon.png";
import SEC_ICON_URL from "images/security-icon.png";
import TEL_ICON_URL from "images/phone-icon.png";
import { emptyScheduleState } from "utils/const";
import { scheduleState } from "atoms";
import { useSetRecoilState } from "recoil";

const serviceCoreTypeData: { [key: string]: any } = {
  HSI: {
    title: "Internet",
    url: INT_ICON_URL,
  },
  TV: {
    title: "Television",
    url: VID_ICON_URL,
  },
  TEL: {
    title: "Telephone",
    url: TEL_ICON_URL,
  },
  SEC: {
    title: "Security",
    url: SEC_ICON_URL,
  },
};

const NOT_FOR_DISPLAY_CATEGORIES = ["Primary", "Included"];

function CartProduct({
  product,
  productDefinition,
  enableRemoval,
  onChangeServiceOptionAmount,
  onRemoveProduct,
  onUpgradeProduct,
  onDowngradeProduct,
}: {
  product: any;
  productDefinition: any;
  enableRemoval: boolean;
  onChangeServiceOptionAmount: (cartServiceOptionId: number, selectedQuantity: number) => void;
  onRemoveProduct: (itemId: number) => void;
  onUpgradeProduct: (cartProductId: number, upgradeProductId: number) => void;
  onDowngradeProduct: (upgradeCartProductId: number) => void;
}) {
  const [chevronUp, setChevronUp] = useState(true);
  const setSchedule = useSetRecoilState(scheduleState);

  const content = productDefinition.contentItemEcommerce.contentEcommerce.reduce((a: any, item: any) => {
    a[item.fieldName] = item;
    return a;
  }, {});

  const serviceOptions = productDefinition.contentItemEcommerce.serviceOptions.reduce((a: any, option: any) => {
    a[option.serviceOptionContent.id] = option.serviceOptionContent;
    return a;
  }, {});

  const displayCategories = product.categories.filter((category: any) => {
    if (product.serviceCoreTypeName === "SEC") {
      return NOT_FOR_DISPLAY_CATEGORIES.concat(["Feature"]).indexOf(category.name) === -1;
    } else {
      return NOT_FOR_DISPLAY_CATEGORIES.indexOf(category.name) === -1;
    }
  });

  const categories = product.categories.reduce((a: any, category: any) => {
    a[category.name] = category;
    return a;
  }, {});

  const displayPrice = categories.Primary.cartServiceOptions[0].displayPrice;
  const price = categories.Primary.cartServiceOptions[0].price;

  const extractVisibleServiceOptions = (category: any): any[] => {
    return category.cartServiceOptions.filter((option: any) => {
      return serviceOptions[option.serviceOptionId] !== undefined;
    });
  };

  return (
    <Container>
      <Row className="mt-1">
        <Col>
          <Accordion className="cart-product-accordion" defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header className="customize-cart-item" onClick={() => setChevronUp(!chevronUp)}>
                <div className="w-100 d-flex">
                  <div style={{ flex: 1 }}>
                    <div>
                      <img
                        className="cart-product-icon me-2"
                        src={serviceCoreTypeData[product.serviceCoreTypeName].url}
                        alt={serviceCoreTypeData[product.serviceCoreTypeName].title}
                      />
                      <span className="h3 align-middle">{serviceCoreTypeData[product.serviceCoreTypeName].title}</span>
                    </div>
                    {enableRemoval && (
                      <small
                        className="d-md-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveProduct(product.id);
                          setSchedule(emptyScheduleState);
                        }}
                      >
                        Remove from cart
                      </small>
                    )}
                  </div>
                  {enableRemoval && (
                    <div className="d-flex justify-content-center flex-column pe-2">
                      <small
                        className="d-none d-md-inline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveProduct(product.id);
                          setSchedule(emptyScheduleState);
                        }}
                      >
                        Remove from cart
                      </small>
                    </div>
                  )}
                  <div className="d-flex justify-content-center flex-column">
                    <i style={{ fontSize: "x-large" }} className={`text-gold fa-sharp fa-solid fa-chevron-${chevronUp ? "up" : "down"}`}></i>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Container>
                  <Row>
                    <Col md={6} className="mb-4 customize-channel-guide">
                      {product.serviceCoreTypeName === "TV" ? <ChannelGuide /> : null}
                    </Col>
                    {product.deviceListURL && (
                      <Col md={6} className="mb-4 text-end channel-listing justify-content-start justify-content-md-end">
                        <a
                          className="btn btn-outline-primary no-radius btn-sm full-channel-list-link-a-tag"
                          href={product.deviceListURL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <i className="fa-sharp fa-regular fa-circle-play me-1"></i>
                          See Compatible Streaming Devices
                        </a>
                      </Col>
                    )}
                  </Row>

                  <Row className="mb-4">
                    <div className="service-option-container">
                      <div className="description">
                        {" "}
                        <h5>{content.summaryHeading.content}</h5>
                        <div dangerouslySetInnerHTML={{ __html: content.summaryCopy?.content }}></div>
                      </div>
                      <div className="price text-start">
                        <div>
                          <strong className={displayPrice === null ? "" : "text-decoration-line-through"}>{price}</strong>
                        </div>
                        <div className="customize-price-text">{displayPrice}</div>
                      </div>
                      <div className="service-option-select">
                        <Button variant="secondary" className="border-bg-pastel-light-blue no-radius d-none" style={{ margin: "0 auto" }}>
                          <i style={{ fontSize: "large" }} className="text-white fa-sharp fa-solid fa-check me-2"></i>
                          Selected
                        </Button>
                      </div>
                    </div>
                  </Row>
                </Container>
                <Container>
                  {product.upgrades?.map((upgrade: any) => (
                    <Row key={upgrade.productId} className="py-3">
                      <div className="service-option-container">
                        <div className="description"> {upgrade.description}</div>
                        <div className="price">
                          <div>
                            <strong className={upgrade.price === null ? "" : "text-decoration-line-through"}>{upgrade.fullPrice}</strong>
                          </div>
                          <div className="customize-price-text">{upgrade.price}</div>
                        </div>
                        <div className="service-option-select">
                          <Button
                            variant="success"
                            className="green-button no-radius"
                            onClick={() => {
                              onUpgradeProduct(product.id, upgrade.productId);
                            }}
                          >
                            Upgrade
                          </Button>
                        </div>
                      </div>
                    </Row>
                  ))}
                  {product.baseProductId && (
                    <Row className="py-2">
                      <Col xs={12} md={8} className="text-center text-md-start">
                        {product.baseProductName}
                      </Col>
                      <Col xs={12} md={2} className="text-center">
                        <div>
                          <strong className={product.baseProductPrice === null ? "" : "text-decoration-line-through"}>
                            {product.baseProductFullPrice}
                          </strong>
                        </div>
                        <div className="customize-price-text">{product.baseProductPrice}</div>
                      </Col>
                      <Col xs={12} md={2} className="text-center text-md-end">
                        <Button
                          variant="dark"
                          className="no-radius"
                          onClick={() => {
                            onDowngradeProduct(product.id);
                          }}
                        >
                          Select
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Container>
                <Container className="mt-4">
                  {displayCategories.map((category: any) => {
                    const visibleServiceOptions = extractVisibleServiceOptions(category);
                    if (visibleServiceOptions.length === 0) {
                      return null;
                    }

                    return (
                      <Container key={category.name} className="py-2">
                        {category?.name ? <strong>{category.name}</strong> : null}
                        <Row>
                          {visibleServiceOptions.map((option: any) => (
                            <Col md={4} key={option.id} className="mb-4">
                              <ServiceOption
                                option={option}
                                optionDefinition={serviceOptions[option.serviceOptionId]}
                                onChangeServiceOptionAmount={onChangeServiceOptionAmount}
                              />
                            </Col>
                          ))}
                        </Row>
                      </Container>
                    );
                  })}
                </Container>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>
      </Row>
    </Container>
  );
}

export default CartProduct;
